import type {
  TConnectionState,
  TWsMessage,
  TWsMessageHandler,
  TWsStateChangeHandler,
  TAuthQueueEntry,
  TWsGetChatsRequest,
  TPendingResponseData,
} from './chat-websocket.types';
import { WS_ACTIONS, RETRY_CONFIG, WS_FETCH_LIMIT } from './utils/chat-websocket.constants';
import { parseWsMessage, calculateRetryDelay } from './utils/chat-websocket.helpers';
import { logInfo, logError } from '@/infra/observability';

let socket: WebSocket | null = null;
let connectionState: TConnectionState = 'idle';
let tries = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

let requestsAwaitingAuth: TAuthQueueEntry[] = [];
let isAuthorizing = false;
// Conexão já autorizada: permite mandar data actions (ex.: getUnreadCount) direto, sem
// re-autorizar — mesmo padrão do send()/Header. Ver ANCR-5329.
let isAuthorized = false;
// Pedido de contagem de não-lidas (getUnreadCount) pendente de autorização.
// O contador das badges das abas vem desta action dedicada (total real),
// não do `unreadChats` paginado do getChats. Ver ANCR-5329.
let unreadCountQueued = false;

const pendingRequestCount: Record<string, number> = { from: 0, to: 0 };

const pendingResponseData: Record<string, TPendingResponseData | null> = { from: null, to: null };

let lastUrl = '';
let lastToken = '';

const messageHandlers: Map<string, TWsMessageHandler> = new Map();
let stateChangeHandler: TWsStateChangeHandler | null = null;

function setConnectionState(newState: TConnectionState): void {
  connectionState = newState;
  stateChangeHandler?.(newState);
}

function clearReconnectTimer(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  clearReconnectTimer();
  const delay = calculateRetryDelay(tries, RETRY_CONFIG.MAX_TRIES, RETRY_CONFIG.DELAY_UNIT_MS);
  tries = Math.min(tries + 1, RETRY_CONFIG.MAX_TRIES);
  logInfo('[WebSocket] scheduling reconnect', { attempt: tries, delayMs: delay });
  reconnectTimer = setTimeout(() => {
    chatWebSocketService.connect(lastUrl, lastToken);
  }, delay);
}

function attachListeners(ws: WebSocket): void {
  ws.onopen = () => {
    tries = 0;
    isAuthorized = false;
    logInfo('[WebSocket] connected');
    setConnectionState('connected');

    if (lastToken) {
      isAuthorizing = true;
      ws.send(JSON.stringify({ action: WS_ACTIONS.authorize, token: lastToken }));
    }
  };

  ws.onerror = () => {
    logError('[WebSocket] connection error');
    setConnectionState('error');
  };

  ws.onclose = () => {
    isAuthorizing = false;
    isAuthorized = false;
    requestsAwaitingAuth = [];
    logInfo('[WebSocket] connection closed');
    setConnectionState('closed');
    scheduleReconnect();
  };

  ws.onmessage = (event: MessageEvent) => {
    const message = parseWsMessage(event.data as string);
    if (!message) return;

    if (message.action === WS_ACTIONS.authorize) {
      isAuthorizing = false;
      isAuthorized = message.statusCode === 200 && Boolean(message.success);
      if (message.statusCode === 200 && message.success) {
        logInfo('[WebSocket] authorized', { queuedRequests: requestsAwaitingAuth.length });
        requestsAwaitingAuth.forEach((request) => {
          const payload: TWsGetChatsRequest = {
            action: WS_ACTIONS.getChats,
            chatType: request.chatType,
            lastKey: request.lastKey,
            limit: WS_FETCH_LIMIT,
          };
          if (request.tags && request.tags.length > 0) {
            payload.tags = request.tags;
          }
          socket?.send(JSON.stringify(payload));
        });
        if (unreadCountQueued) {
          socket?.send(JSON.stringify({ action: WS_ACTIONS.getUnreadCount }));
          unreadCountQueued = false;
        }
      }
      requestsAwaitingAuth = [];
      messageHandlers.get(message.action)?.(message);
      return;
    }

    messageHandlers.get(message.action)?.(message);
  };
}

/** Serviço singleton para conexão WebSocket do chat com reconexão automática e fila de autenticação. */
export const chatWebSocketService = {
  connect(url: string, token: string): void {
    if (socket) {
      chatWebSocketService.close();
    }

    lastUrl = url;
    lastToken = token;
    logInfo('[WebSocket] connecting', { url });
    setConnectionState('connecting');

    try {
      socket = new WebSocket(url);
      attachListeners(socket);
    } catch (err) {
      logError('[WebSocket] failed to create connection', { url, error: String(err) });
      setConnectionState('error');
      scheduleReconnect();
    }
  },

  close(): void {
    clearReconnectTimer();
    if (socket) {
      socket.onopen = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.onmessage = null;
      try {
        // WebSocket.close() throws if called while still in CONNECTING state
        if (socket.readyState !== WebSocket.CONNECTING) {
          socket.close();
        }
      } catch {
        // WebSocket.close() may throw in CONNECTING state — safe to ignore
      }
      socket = null;
    }
    isAuthorizing = false;
    isAuthorized = false;
    requestsAwaitingAuth = [];
    unreadCountQueued = false;
    setConnectionState('idle');
  },

  send(event: TWsMessage): void {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    socket.send(JSON.stringify(event));
  },

  sendAuthorized(entry: TAuthQueueEntry): void {
    requestsAwaitingAuth.push(entry);

    if (!isAuthorizing && socket && socket.readyState === WebSocket.OPEN) {
      isAuthorizing = true;
      socket.send(JSON.stringify({ action: WS_ACTIONS.authorize, token: lastToken }));
    }
  },

  /**
   * Solicita a contagem de não-lidas (getUnreadCount) — fonte do contador das badges
   * das abas. É uma data action simples: manda DIRETO no socket já autorizado (igual ao
   * send()/Header), sem re-autorizar — re-autorizar atrasava/segurava a resposta. Se
   * ainda não autorizou, enfileira e dispara no sucesso da próxima auth. Ver ANCR-5329.
   */
  requestUnreadCount(): void {
    if (socket && socket.readyState === WebSocket.OPEN && isAuthorized) {
      socket.send(JSON.stringify({ action: WS_ACTIONS.getUnreadCount }));
      return;
    }
    unreadCountQueued = true;
  },

  onMessage(actions: string[], handler: TWsMessageHandler): void {
    actions.forEach((action) => messageHandlers.set(action, handler));
  },

  onStateChange(handler: TWsStateChangeHandler): void {
    stateChangeHandler = handler;
  },

  getConnectionState(): TConnectionState {
    return connectionState;
  },

  resetRetries(): void {
    tries = 0;
  },

  resetPendingRequestCount(chatType: string): void {
    pendingRequestCount[chatType] = 0;
  },

  incrementPendingRequestCount(chatType: string): void {
    pendingRequestCount[chatType]++;
  },

  decrementPendingRequestCount(chatType: string): void {
    if (pendingRequestCount[chatType] > 0) {
      pendingRequestCount[chatType]--;
    }
  },

  getPendingRequestCount(chatType: string): number {
    return pendingRequestCount[chatType] ?? 0;
  },

  setPendingResponseData(chatType: string, data: TPendingResponseData | null): void {
    pendingResponseData[chatType] = data;
  },

  getPendingResponseData(chatType: string): TPendingResponseData | null {
    return pendingResponseData[chatType] ?? null;
  },
};
